import { Loader, useThemeContext } from '@huggg/react-components';
import classnames from 'classnames';
import GuidedChoiceFlow from 'components/GuidedChoice/GuidedChoiceFlow';
import ConfirmationBody from 'components/Redeem/Method/Voucher/Segments/ConfirmationBody';
import ConfirmationHeader from 'components/Redeem/Method/Voucher/Segments/ConfirmationHeader';
import voucherStyles from 'components/Redeem/Method/Voucher/voucher.module.scss';
import { ConfirmationDialog } from 'components/ui';
import dictionary from 'config/dictionary';
import { withHuggg, withRedirect } from 'hoc';
import { Layout } from 'layouts';
import PropTypes from 'prop-types';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { connect } from 'react-redux';
import { useHistory } from 'react-router-dom/cjs/react-router-dom.min';
import { compose } from 'redux';
import { getBrand } from 'redux/modules/brand';
import {
  getSegments,
  getRedeemedSegment,
  fetchHuggg,
} from 'redux/modules/huggg';
import {
  getDefaultSegmentTerritory,
  getSelectedSegment,
  setSelectedSegment,
} from 'redux/modules/selectedSegment';
import {
  clearError,
  generate as generateVoucher,
  getError,
  getIsGenerating,
} from 'redux/modules/voucher';
import { voucherClaimErrorContent } from 'utilities/errorHandling';
import { getBasePathname } from 'utilities/huggg';
import { getRedemptionMethodID } from 'utilities/redemption';
import SegmentCarousel from '../../components/Redeem/Method/Voucher/Segments/SegmentCarousel';
import styles from './choose.module.scss';

export const ChooseUnconnected = (props) => {
  const {
    multiSegmentFlow,
    selectedSegment,
    segments,
    setSelectedSegment,
    huggg,
    brand,
    generateVoucher,
    defaultTerritory,
    fetchHuggg,
    voucherError,
    isGenerating = false,
    clearError,
  } = props;
  const confirmDialogRef = useRef();
  const history = useHistory();
  const { operator } = useThemeContext();

  useEffect(() => {
    if (huggg.shortcode && !segments?.length) {
      fetchHuggg(huggg.shortcode);
    }
  }, [huggg.shortcode, segments, fetchHuggg]);

  const isGuidedChoice = useMemo(
    () => multiSegmentFlow === 'guided-choice',
    [multiSegmentFlow],
  );
  const segmentName = useMemo(
    () => (selectedSegment ? selectedSegment.name : ''),
    [selectedSegment],
  );

  const [nestedSegmentSelected, setNestedSegmentSelected] = useState(false);
  const shouldShowConfirmation = (segment) =>
    segment &&
    !['form-redemption', 'pin4', 'prepaid-card', 'bank-payment'].includes(
      segment.voucherType,
    );

  const onSelectSegment = (segment, isNested = false) => {
    if (!segment || !segment.id) {
      console.error('Choose: Attempting to select invalid segment:', segment);
      return;
    }

    console.log('Choose: onSelectSegment', {
      segment,
    });

    // A claim is still in flight (the request guard would drop a second one
    // anyway). Ignore the pick so selectedSegment keeps describing the claim
    // whose outcome, and error copy, the recipient is about to see.
    if (isGenerating) {
      return;
    }

    if (voucherError) {
      clearError();
    }

    setNestedSegmentSelected(isNested);
    setSelectedSegment(segment);

    if (shouldShowConfirmation(segment)) {
      confirmDialogRef.current.open();
    } else {
      history.push(`${getBasePathname(huggg)}/redeem`);
    }
  };

  const onConfirmationConfirmClick = () => {
    confirmDialogRef.current.close();

    if (selectedSegment.voucherType === 'tillo') {
      generateVoucher(
        huggg.id,
        selectedSegment?.id,
        getRedemptionMethodID(huggg),
        { territory: defaultTerritory },
      );
    } else {
      history.push(`${getBasePathname(huggg)}/redeem`);
    }
  };

  const onConfirmationCancelClick = () => confirmDialogRef.current.close();

  if (!selectedSegment && !segments?.length) {
    return <Loader />;
  }

  // A failed tillo claim used to close the dialog and show nothing. Share the
  // redeem screen's copy so the recipient knows whether to retry or contact CS.
  const claimError =
    voucherError &&
    voucherClaimErrorContent({
      error: voucherError,
      voucherType: selectedSegment?.voucherType,
      brandName: brand?.name,
      operator,
    });

  return (
    <Layout>
      <ConfirmationDialog
        data-test-id="confirmation-modal"
        body={<ConfirmationBody brandName={brand.name} />}
        cancelButtonText={dictionary.segments.confirmation.cancelButton}
        confirmButtonText={dictionary.segments.confirmation.confirmButton}
        header={<ConfirmationHeader segmentName={segmentName} />}
        onConfirm={onConfirmationConfirmClick}
        onCancel={onConfirmationCancelClick}
        ref={confirmDialogRef}
        huggg={huggg}
        segment={selectedSegment ?? segments[0]}
        nestedSegmentSelected={nestedSegmentSelected}
      />
      {claimError && (
        <div
          className={classnames(voucherStyles.error, styles.error)}
          data-test-id="choose-error"
        >
          {claimError}
        </div>
      )}
      {isGenerating && <Loader data-test-id="choose-loader" />}
      {isGuidedChoice ? (
        <GuidedChoiceFlow
          data-test-id="guided-choice-flow"
          onSelectSegment={onSelectSegment}
          disabled={isGenerating}
        />
      ) : (
        <SegmentCarousel
          data-test-id="segment-carousel"
          onSelectSegment={onSelectSegment}
          disabled={isGenerating}
        />
      )}
    </Layout>
  );
};

const mapStateToProps = (state) => {
  return {
    segments: getSegments(state),
    redeemedSegment: getRedeemedSegment(state),
    selectedSegment: getSelectedSegment(state),
    brand: getBrand(state),
    defaultTerritory: getDefaultSegmentTerritory(state),
    voucherError: getError(state),
    isGenerating: getIsGenerating(state),
  };
};

export const outOfStockRedirectHandler = (props) => {
  return [
    props.isOutOfStock,
    `${getBasePathname(props.huggg)}/more-information`,
  ];
};

export const redeemedRedirectHandler = (props) => {
  return [
    props.isRedeemed || props.isFetchedHuggg,
    `${getBasePathname(props.huggg)}/redeemed`,
  ];
};

export const redeemableRedirectHandler = (props) => [
  !props.isRedeemable,
  `${getBasePathname(props.huggg)}/stash`,
];

export const redeemRedirectHandler = (props) => {
  const { segments, huggg, redeemedSegment } = props;
  const isSingleSegment =
    segments?.length === 1 && segments[0].itemType === 'segment';
  return [
    isSingleSegment || Boolean(redeemedSegment),
    `${getBasePathname(huggg)}/redeem`,
  ];
};

export default compose(
  withHuggg,
  connect(mapStateToProps, {
    setSelectedSegment,
    generateVoucher,
    fetchHuggg,
    clearError,
  }),
  withRedirect(redeemedRedirectHandler),
  withRedirect(redeemableRedirectHandler),
  withRedirect(redeemRedirectHandler),
  withRedirect(outOfStockRedirectHandler),
)(ChooseUnconnected);

ChooseUnconnected.propTypes = {
  multiSegmentFlow: PropTypes.string,
  selectedSegment: PropTypes.object,
  segments: PropTypes.array,
  setSelectedSegment: PropTypes.func.isRequired,
  huggg: PropTypes.object.isRequired,
  brand: PropTypes.object.isRequired,
  fetchHuggg: PropTypes.func.isRequired,
  generateVoucher: PropTypes.func.isRequired,
  voucherError: PropTypes.oneOfType([PropTypes.object, PropTypes.string]),
  isGenerating: PropTypes.bool,
  clearError: PropTypes.func,
};
